import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { TypedSignaturePad } from '@/components/common/TypedSignaturePad';
import { API } from '@/utils/constants';

/**
 * iPad Kiosk Page - Customer-facing signature terminal
 * States: waiting -> signature -> thankyou -> waiting
 */
const KioskPage = () => {
  const [state, setState] = useState('waiting'); // waiting, signature, thankyou
  const [job, setJob] = useState(null);
  const [termsAndConditions, setTermsAndConditions] = useState('');
  const [businessInfo, setBusinessInfo] = useState({});
  const [thankYouDuration, setThankYouDuration] = useState(5);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Poll for pending jobs
  useEffect(() => {
    const checkPendingJob = async () => {
      if (state !== 'waiting') return;
      
      try {
        const response = await axios.get(`${API}/kiosk/pending`);
        if (response.data.pending && response.data.job) {
          setJob(response.data.job);
          setTermsAndConditions(response.data.terms_and_conditions || '');
          setBusinessInfo(response.data.business_info || {});
          setState('signature');
        }
      } catch (error) {
        console.error('Error checking pending job:', error);
      }
    };

    // Poll every 2 seconds when in waiting state
    const interval = setInterval(checkPendingJob, 2000);
    checkPendingJob(); // Initial check

    return () => clearInterval(interval);
  }, [state]);

  // Fetch settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // We can't fetch settings without auth, so we'll use defaults
        // The backend will provide settings with the pending job
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const handleCancel = async () => {
    if (!job) return;
    
    try {
      await axios.post(`${API}/kiosk/cancel/${job.id}`);
    } catch (error) {
      console.error('Error cancelling:', error);
    }
    
    setJob(null);
    setState('waiting');
    setAgreedToTerms(false);
  };

  // Submit typed signature (rendered to PNG data URL by TypedSignaturePad) plus
  // audit metadata. Backend also captures IP + UA from request headers.
  const handleSubmitSignature = async (signatureData, meta = {}) => {
    if (!job) return;
    try {
      await axios.post(`${API}/kiosk/sign/${job.id}`, {
        signature: signatureData,
        signature_type: 'typed',
        typed_name: meta.typed_name || '',
      });

      setState('thankyou');

      // Return to waiting after thank you duration
      setTimeout(() => {
        setJob(null);
        setState('waiting');
        setAgreedToTerms(false);
      }, thankYouDuration * 1000);

    } catch (error) {
      console.error('Error submitting signature:', error);
      alert('Failed to submit signature. Please try again.');
      throw error;   // Let the pad component re-enable its Submit button
    }
  };

  // Waiting State
  if (state === 'waiting') {
    return (
      <div 
        className="kiosk-waiting" 
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #60a5fa 100%)',
          color: 'white',
          padding: '2rem',
          textAlign: 'center'
        }}
        data-testid="kiosk-waiting"
      >
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ 
            width: '120px', 
            height: '120px', 
            background: 'rgba(255,255,255,0.2)', 
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.5rem',
            fontSize: '3rem'
          }}>
            🖥️
          </div>
          <h1 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '0.5rem' }}>
            {businessInfo?.business_name || 'Total Data Recall'}
          </h1>
          <p style={{ fontSize: '1.25rem', opacity: '0.9' }}>
            Job Management System
          </p>
        </div>
        
        <div style={{ 
          background: 'rgba(255,255,255,0.15)', 
          borderRadius: '1rem', 
          padding: '2rem 3rem',
          backdropFilter: 'blur(10px)'
        }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>
            Ready for Customer
          </h2>
          <p style={{ fontSize: '1rem', opacity: '0.8' }}>
            Waiting for job to be sent...
          </p>
          <div style={{ 
            marginTop: '1.5rem',
            display: 'flex',
            gap: '0.5rem',
            justifyContent: 'center'
          }}>
            <span style={{ width: '12px', height: '12px', background: 'white', borderRadius: '50%', animation: 'pulse 1.5s ease-in-out infinite' }}></span>
            <span style={{ width: '12px', height: '12px', background: 'white', borderRadius: '50%', animation: 'pulse 1.5s ease-in-out infinite 0.3s' }}></span>
            <span style={{ width: '12px', height: '12px', background: 'white', borderRadius: '50%', animation: 'pulse 1.5s ease-in-out infinite 0.6s' }}></span>
          </div>
        </div>

        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 0.3; transform: scale(0.8); }
            50% { opacity: 1; transform: scale(1); }
          }
        `}</style>
      </div>
    );
  }

  // Thank You State
  if (state === 'thankyou') {
    return (
      <div 
        className="kiosk-thankyou" 
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #059669 0%, #10b981 50%, #34d399 100%)',
          color: 'white',
          padding: '2rem',
          textAlign: 'center'
        }}
        data-testid="kiosk-thankyou"
      >
        <div style={{ 
          width: '150px', 
          height: '150px', 
          background: 'rgba(255,255,255,0.2)', 
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '2rem',
          fontSize: '4rem'
        }}>
          ✅
        </div>
        
        <h1 style={{ fontSize: '3rem', fontWeight: '700', marginBottom: '1rem' }}>
          Thank You!
        </h1>
        
        <p style={{ fontSize: '1.5rem', marginBottom: '0.5rem', opacity: '0.95' }}>
          Your job has been booked in.
        </p>
        
        <div style={{ 
          background: 'rgba(255,255,255,0.2)', 
          borderRadius: '1rem', 
          padding: '1.5rem 2rem',
          marginTop: '1.5rem'
        }}>
          <p style={{ fontSize: '1.25rem', fontWeight: '600' }}>
            Job Number: #{job?.job_number}
          </p>
        </div>
        
        <p style={{ fontSize: '1.1rem', marginTop: '2rem', opacity: '0.9', maxWidth: '400px' }}>
          We will contact you when your device is ready for collection.
        </p>
      </div>
    );
  }

  // Signature State
  return (
    <div 
      className="kiosk-signature" 
      style={{
        minHeight: '100vh',
        background: '#f3f4f6',
        padding: '1rem',
        overflowY: 'auto'
      }}
      data-testid="kiosk-signature"
    >
      {/* Header */}
      <div style={{ 
        background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
        color: 'white',
        padding: '1.25rem',
        borderRadius: '0.75rem',
        marginBottom: '1rem',
        textAlign: 'center'
      }}>
        <h1 style={{ fontSize: '1.75rem', fontWeight: '700', margin: 0 }}>
          📋 Job Card #{job?.job_number}
        </h1>
        <p style={{ margin: '0.5rem 0 0', opacity: '0.9', fontSize: '1rem' }}>
          Please review and sign below
        </p>
      </div>

      {/* Job Details */}
      <Card style={{ marginBottom: '1rem' }}>
        <CardContent style={{ padding: '1.25rem' }}>
          <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#1e40af', marginBottom: '0.75rem', textTransform: 'uppercase' }}>
            Customer Details
          </h3>
          <div style={{ display: 'grid', gap: '0.5rem', fontSize: '1.1rem' }}>
            <div><strong>Name:</strong> {job?.customer_name}</div>
            <div><strong>Phone:</strong> {job?.mobile}</div>
          </div>
        </CardContent>
      </Card>

      <Card style={{ marginBottom: '1rem' }}>
        <CardContent style={{ padding: '1.25rem' }}>
          <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#1e40af', marginBottom: '0.75rem', textTransform: 'uppercase' }}>
            Device Information
          </h3>
          <div style={{ display: 'grid', gap: '0.5rem', fontSize: '1.1rem' }}>
            <div><strong>Brand:</strong> {job?.brand || '-'}</div>
            <div><strong>Model:</strong> {job?.model_number || '-'}</div>
            <div><strong>Serial/IMEI:</strong> {job?.serial_number || '-'}</div>
            {((Array.isArray(job?.accessories) && job.accessories.length > 0) || (typeof job?.accessories === 'string' && job?.accessories)) && (
              <div><strong>Accessories:</strong> {Array.isArray(job.accessories) ? job.accessories.join(', ') : job.accessories}</div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card style={{ marginBottom: '1rem' }}>
        <CardContent style={{ padding: '1.25rem' }}>
          <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#1e40af', marginBottom: '0.75rem', textTransform: 'uppercase' }}>
            Job Description
          </h3>
          <p style={{ fontSize: '1.1rem', margin: 0, whiteSpace: 'pre-wrap' }}>
            {job?.job_description || 'No description provided'}
          </p>
        </CardContent>
      </Card>

      {/* Terms & Conditions */}
      <Card style={{ marginBottom: '1rem', background: '#fefce8', border: '1px solid #fef08a' }}>
        <CardContent style={{ padding: '1.25rem' }}>
          <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#854d0e', marginBottom: '0.75rem', textTransform: 'uppercase' }}>
            📜 Terms & Conditions
          </h3>
          <p style={{ fontSize: '1rem', margin: 0, whiteSpace: 'pre-wrap', color: '#713f12', lineHeight: '1.6' }}>
            {termsAndConditions || 'No terms and conditions configured.'}
          </p>
        </CardContent>
      </Card>

      {/* Terms Agreement Checkbox */}
      {termsAndConditions && (
        <Card style={{ marginBottom: '1rem', background: '#fef3c7', border: '2px solid #fbbf24' }}>
          <CardContent style={{ padding: '1.25rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '1.05rem' }}>
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                style={{ width: '22px', height: '22px', marginRight: '0.75rem', cursor: 'pointer' }}
                data-testid="kiosk-agree-terms-checkbox"
              />
              <span style={{ fontWeight: 500, color: '#78350f' }}>
                I have read and agree to the Terms &amp; Conditions above
              </span>
            </label>
          </CardContent>
        </Card>
      )}

      {/* Typed Signature Pad — replaces old freehand canvas. Same UX as the
          email signature link so customers see one flow whether they sign at
          the counter (iPad) or later at home (email link). */}
      <Card style={{ marginBottom: '1rem' }}>
        <CardContent style={{ padding: '1.25rem' }}>
          <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#1e40af', marginBottom: '0.75rem', textTransform: 'uppercase' }}>
            ✏️ Customer Signature
          </h3>
          <TypedSignaturePad
            onSave={handleSubmitSignature}
            onCancel={handleCancel}
            initialName={job?.customer_name || ''}
            disabled={!!termsAndConditions && !agreedToTerms}
            disabledReason="Please tick the Terms & Conditions box above before signing."
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default KioskPage;
